import { mapAgentEditChromeRanges } from '@atlaskit/editor-common/collab-agent-edit-chrome-ranges';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { Transform } from '@atlaskit/editor-prosemirror/transform';
import type { Mapping, StepMap } from '@atlaskit/editor-prosemirror/transform';

import type { AgentEditChromeState } from './types';

export const mapAgentEditChromeState = (
	state: AgentEditChromeState,
	mapping: Mapping,
): AgentEditChromeState => {
	if (!state.ranges.length) {
		return state;
	}

	return {
		...state,
		ranges: state.ranges
			.map(({ from, to }) => ({
				from: mapping.map(from, -1),
				to: mapping.map(to, 1),
			}))
			.filter(({ from, to }) => from < to),
	};
};

const betweenDocuments = (before: PMNode, after: PMNode): Transform => {
	const tr = new Transform(before);
	const start = before.content.findDiffStart(after.content);
	const end = before.content.findDiffEnd(after.content);
	if (start === null || !end) {
		return tr;
	}
	const overlap = Math.max(0, start - Math.min(end.a, end.b));
	return tr.replace(start, end.a + overlap, after.slice(start, end.b + overlap));
};

const changesOnlyTextblockContent = (before: PMNode, after: PMNode, map: StepMap): boolean => {
	let inlineOnly = true;
	map.forEach((from, to, nextFrom, nextTo) => {
		const $from = before.resolve(from);
		const $to = before.resolve(to);
		const $nextFrom = after.resolve(nextFrom);
		const $nextTo = after.resolve(nextTo);
		if (
			!$from.parent.isTextblock ||
			!$from.sameParent($to) ||
			!$nextFrom.parent.isTextblock ||
			!$nextFrom.sameParent($nextTo) ||
			$from.parent.type !== $nextFrom.parent.type
		) {
			inlineOnly = false;
		}
	});
	return inlineOnly;
};

// Rebase ordinary edits outside the temporary replacement into the durable
// snapshot. Positions erased inside a preview cannot be recovered safely.
export const mapAgentEditChromePreview = (
	preview: NonNullable<AgentEditChromeState['preview']>,
	tr: Pick<Transaction, 'docs' | 'steps'>,
): NonNullable<AgentEditChromeState['preview']> => {
	let doc = preview.doc;
	let ranges = preview.rangesAreTextblocks
		? preview.ranges
		: mapAgentEditChromeRanges(new Transform(doc), preview.ranges).map(({ from, to }) => ({
				from: from + 1,
				to: to - 1,
			}));
	let rangesAreTextblocks = true;
	for (let index = 0; index < tr.steps.length; index++) {
		const projection = betweenDocuments(doc, tr.docs[index]).mapping;
		const step = tr.steps[index];
		let outsidePreview = true;
		projection.maps.forEach((map) => {
			map.forEach((_oldStart, _oldEnd, from, to) => {
				step.getMap().forEach((start, end) => {
					if ((start < to && end > from) || (start === end && start > from && start < to)) {
						outsidePreview = false;
					}
				});
			});
		});
		const rebased = outsidePreview ? step.map(projection.invert()) : null;
		const change = new Transform(doc);
		if (rebased && !change.maybeStep(rebased).failed) {
			rangesAreTextblocks =
				rangesAreTextblocks && changesOnlyTextblockContent(doc, change.doc, rebased.getMap());
			doc = change.doc;
			ranges = mapAgentEditChromeState({ ranges, revision: 0 }, change.mapping).ranges;
		} else {
			// Drop only touched blocks when an edit lies inside an erased preview
			// span. Separate unaffected activity can still survive restoration.
			ranges = ranges.filter((range) => {
				const from = projection.map(range.from, -1);
				const to = projection.map(range.to, 1);
				let retained = true;
				step.getMap().forEach((start, end) => {
					if ((start < to && end > from) || (start === end && start > from && start < to)) {
						retained = false;
					}
				});
				return retained;
			});
		}
	}
	return { doc, ranges, rangesAreTextblocks };
};

// Compare durable activity directly with restored content. Preview step maps have
// already discarded replaced positions and cannot recover them on restoration.
export const restoreAgentEditChromeRanges = (
	preview: NonNullable<AgentEditChromeState['preview']>,
	doc: PMNode,
): AgentEditChromeState['ranges'] => {
	const tr = betweenDocuments(preview.doc, doc);
	return tr.docChanged ? mapAgentEditChromeRanges(tr, preview.ranges) : preview.ranges;
};
